import { assert } from 'assert-ts';
import { Suspense } from 'react';
import { useSuspenseRelayQuery } from '#api/relays.queries.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Code } from '#components/atoms/code/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { useActiveCount } from '#hooks/useActiveCount.ts';
import { useIsManagedLighthouseCovered } from '#hooks/useIsManagedLighthouseCovered.ts';
import { useDeleteHostMutation } from '#screens/hosts/delete/useDeleteHostMutation.ts';
import { isInfraHost } from '#util/host.ts';

type Props = {
  relayId: string;
  onCancel: () => void;
  onDelete: () => void;
};

export function DeleteRelayDialog(props: Props) {
  // The boundary keeps the active-count fetch from bubbling to the page boundary, which would blank
  // the page on dialog open.
  return (
    <Suspense fallback={null}>
      <DeleteRelayDialogContent {...props} />
    </Suspense>
  );
}

function DeleteRelayDialogContent({ relayId, onCancel, onDelete }: Props) {
  const deleteRelay = useDeleteHostMutation();
  const relay = useSuspenseRelayQuery(relayId).data.data;
  assert(isInfraHost(relay), 'DeleteRelayDialog requires a relay');
  const { isLastActive: isLastRelay } = useActiveCount(relay);
  const isMtlhCovered = useIsManagedLighthouseCovered(relay.networkID);

  return (
    <ConfirmDialog
      title={<div className="truncate">Delete {relay.name}?</div>}
      disableAccept={deleteRelay.isPending}
      acceptButtonText="Delete"
      onCancel={onCancel}
      onAccept={() => {
        deleteRelay.mutate(relayId, {
          onSuccess: onDelete,
        });
      }}
      shouldRestoreFocusViaFocusScope={true}
    >
      {isLastRelay && isMtlhCovered ?
        <Banner variant="info" className="mb-24">
          This is your only dedicated relay. Managed lighthouses still relay traffic on this network.
        </Banner>
      : isLastRelay ?
        <Banner variant="alert" className="mb-24">
          This is your only active dedicated relay in this network.
        </Banner>
      : null}
      <p className="p mb-0">
        Are you sure you want to delete <Code className="ml-6 wrap-anywhere">{relay.name}</Code> (
        {relay.ipAddresses.map((ip, idx) => (
          <span key={ip}>
            {idx > 0 && ', '}
            <Code>{ip}</Code>
          </span>
        ))}
        )?
      </p>
    </ConfirmDialog>
  );
}
